import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Wallet, KeyRound, ChevronLeft, ShieldCheck, Info, Ticket, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const { planId } = useParams();
  const [sp] = useSearchParams();
  const action = sp.get("action") || "renew";
  const navigate = useNavigate();
  const { user } = useAuth();

  const [plan, setPlan] = useState(null);
  const [gateway, setGateway] = useState("razorpay");
  const [initData, setInitData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Coupon: entered code (uppercase) + validation result from server.
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/plans");
        setPlan((data.items || []).find(p => p.id === planId));
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    })();
  }, [planId]);

  const applyCoupon = async () => {
    const code = (couponInput || "").trim().toUpperCase();
    if (!code) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/saas/coupons/validate", {
        code, plan_id: planId, hospital_id: user.hospital_id,
      });
      setCouponApplied(data);
      toast.success(`${code} applied — you save ₹${Number(data.discount_amount).toLocaleString("en-IN")}`);
    } catch (e) {
      setCouponApplied(null);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Coupon not accepted");
    } finally { setCouponBusy(false); }
  };

  const clearCoupon = () => { setCouponApplied(null); setCouponInput(""); };

  const initiate = async () => {
    setLoading(true);
    try {
      const body = { plan_id: planId, gateway, action };
      if (couponApplied?.code) body.coupon_code = couponApplied.code;
      const { data } = await api.post("/payments/checkout/init", body);
      setInitData(data);
      if (data.gateway === "razorpay" && data.razorpay?.mode === "live") {
        if (!data.razorpay?.order_id) {
          // Server couldn't create a Razorpay Order (bad keys, network,
          // Razorpay outage). Refuse to open Checkout — otherwise UPI success
          // will fail signature verification and debit the user's money for
          // nothing. See production bug on 2026-08-31.
          toast.error(
            `Cannot start payment — ${data.razorpay?.error || "Razorpay Orders API unreachable"}. Please contact support.`,
            { duration: 15000 },
          );
          return;
        }
        openRazorpay(data.razorpay, data.transaction.txnid);
      } else if (data.gateway === "payu" && data.payu?.mode === "live") {
        submitPayuForm(data.payu);
      } else {
        toast.message("Gateway not configured — Super Admin must set keys first.", { description: "Showing checkout in DEMO/stub mode." });
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const openRazorpay = (rp, txnid) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => {
      const options = {
        key: rp.key_id,
        amount: rp.amount,
        currency: rp.currency,
        name: rp.name,
        description: rp.description,
        order_id: rp.order_id,
        prefill: rp.prefill,
        notes: rp.notes,
        handler: async (response) => {
          try {
            await api.post("/payments/razorpay/verify", {
              txnid,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful — subscription extended");
            navigate("/app/my-plan");
          } catch (e) {
            // Payment succeeded at Razorpay but our verify failed. Show the
            // reference IDs the user needs to email support for reconciliation.
            toast.error(
              `Payment verification failed. Money already debited — DO NOT PAY AGAIN. Email support with: Txn ${txnid}, Payment ${response.razorpay_payment_id}`,
              { duration: 20000 },
            );
          }
        },
        theme: { color: "#0F4C81" },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    };
    document.body.appendChild(script);
  };

  const submitPayuForm = (payu) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = payu.action_url;
    Object.entries(payu.params).forEach(([k, v]) => {
      const input = document.createElement("input");
      input.type = "hidden"; input.name = k; input.value = String(v || "");
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  if (!plan) return <div className="p-8 text-sm text-ink-muted">Loading plan…</div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="checkout-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/app/my-plan")}><ChevronLeft size={14} className="mr-1" />Back to my plan</Button>

      <div>
        <div className="section-label">Checkout</div>
        <h1 className="font-heading text-3xl font-semibold capitalize">{action} subscription</h1>
      </div>

      <Card className="p-6">
        <h2 className="font-heading text-xl font-semibold">{plan.name}</h2>
        {plan.description && <p className="text-sm text-ink-muted mt-1">{plan.description}</p>}
        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="flex items-baseline gap-2">
              <p className="font-heading text-4xl" data-testid="checkout-price">₹ {Number(plan.price_inr).toLocaleString("en-IN")}</p>
              {plan.mrp_inr > 0 && plan.mrp_inr > plan.price_inr && (
                <p className="text-lg text-ink-muted line-through" data-testid="checkout-mrp">₹ {Number(plan.mrp_inr).toLocaleString("en-IN")}</p>
              )}
              {plan.mrp_inr > 0 && plan.mrp_inr > plan.price_inr && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid="checkout-discount-badge">
                  {Math.round(((plan.mrp_inr - plan.price_inr) / plan.mrp_inr) * 100)}% off
                </span>
              )}
            </div>
            <p className="text-sm text-ink-muted mt-1 capitalize">per {(plan.duration || "year").replace("_", "-")} · {plan.validity_months} months validity</p>
          </div>
          <Badge variant="outline" className="border-brand bg-brand/10 text-brand">{plan.branch_limit} branches · {plan.user_limit} users</Badge>
        </div>

        {/* Coupon input — validates on Apply, doesn't reserve. */}
        <div className="mt-5 rounded-md border border-dashed border-brand/40 bg-brand/5 p-3" data-testid="coupon-block">
          {!couponApplied ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-brand">
                  <Ticket size={12} />Have a coupon code?
                </div>
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                  placeholder="e.g. DIWALI25"
                  className="w-full rounded-md border border-border bg-white px-2.5 py-1.5 text-sm font-mono uppercase tracking-wider"
                  data-testid="coupon-input"
                />
              </div>
              <Button onClick={applyCoupon} disabled={!couponInput.trim() || couponBusy} className="bg-brand hover:bg-brand-700" data-testid="coupon-apply-btn">
                {couponBusy ? "Checking…" : "Apply"}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between" data-testid="coupon-applied">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Applied</span>
                <span className="font-mono font-semibold text-brand">{couponApplied.code}</span>
                <span className="text-xs text-ink-muted">
                  ({couponApplied.discount_type === "percent" ? `${couponApplied.discount_value}% off` : `₹${couponApplied.discount_value} off`})
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-emerald-700" data-testid="coupon-savings">
                  −₹ {Number(couponApplied.discount_amount).toLocaleString("en-IN")}
                </span>
                <button onClick={clearCoupon} className="text-ink-muted hover:text-rose-700" title="Remove coupon" data-testid="coupon-remove-btn">
                  <X size={14} />
                </button>
              </div>
            </div>
          )}
          {couponApplied && (
            <div className="mt-3 space-y-1 border-t border-brand/20 pt-2 text-sm">
              <div className="flex justify-between text-ink-muted"><span>Subtotal</span><span>₹ {Number(plan.price_inr).toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-emerald-700"><span>Coupon discount</span><span>−₹ {Number(couponApplied.discount_amount).toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between font-heading text-base font-semibold" data-testid="coupon-final-amount">
                <span>Total payable</span><span>₹ {Number(couponApplied.final_amount).toLocaleString("en-IN")}</span>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-heading text-lg font-semibold">Choose payment method</h3>
        <p className="text-xs text-ink-muted mt-1">Secure payment processing — no card data is stored by CareO.</p>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className={`cursor-pointer rounded-lg border p-4 transition ${gateway === "razorpay" ? "border-brand bg-brand/5 ring-2 ring-brand/20" : "border-border hover:bg-secondary/30"}`}>
            <input type="radio" name="gw" value="razorpay" checked={gateway === "razorpay"} onChange={() => setGateway("razorpay")} className="sr-only" data-testid="gw-razorpay" />
            <div className="flex items-center gap-3">
              <Wallet className="text-brand" />
              <div>
                <p className="font-semibold">Razorpay</p>
                <p className="text-xs text-ink-muted">Cards · UPI · Netbanking · Wallets</p>
              </div>
            </div>
          </label>
          <label className={`cursor-pointer rounded-lg border p-4 transition ${gateway === "payu" ? "border-brand bg-brand/5 ring-2 ring-brand/20" : "border-border hover:bg-secondary/30"}`}>
            <input type="radio" name="gw" value="payu" checked={gateway === "payu"} onChange={() => setGateway("payu")} className="sr-only" data-testid="gw-payu" />
            <div className="flex items-center gap-3">
              <KeyRound className="text-brand" />
              <div>
                <p className="font-semibold">PayU India</p>
                <p className="text-xs text-ink-muted">UPI · Cards · Netbanking</p>
              </div>
            </div>
          </label>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <ShieldCheck size={14} className="text-emerald-600" />
            256-bit SSL · PCI-DSS compliant gateways
          </div>
          <Button onClick={initiate} disabled={loading} className="bg-brand hover:bg-brand-700" data-testid="pay-now-btn">
            {loading ? "Initiating…" : `Pay ₹ ${Number(couponApplied?.final_amount ?? plan.price_inr).toLocaleString("en-IN")}`}
          </Button>
        </div>

        {initData && initData[gateway]?.mode === "stub" && (
          <Card className="mt-4 border-amber-300 bg-amber-50 p-3">
            <div className="flex items-start gap-2 text-amber-900 text-sm">
              <Info size={16} className="mt-0.5" />
              <div>
                <p className="font-semibold">Gateway not configured (Demo mode)</p>
                <p className="text-xs mt-1">Super Admin needs to add {gateway === "razorpay" ? "Razorpay" : "PayU"} credentials in <b>Super Admin → Payment Gateways</b> before live payments can flow. Transaction ID: <span className="font-mono">{initData.transaction.txnid}</span></p>
              </div>
            </div>
          </Card>
        )}
      </Card>
    </div>
  );
}
